/**
 * Graph helpers shared by autocomplete and save-time validation.
 *
 * Both code paths need predecessor sets — the autocomplete to filter
 * "which node ids can I reference here?" and the validator to check
 * "is this reference reachable from where I'm typing it?". Pulling the
 * BFS out of WorkflowEditorPage avoids two copies that can drift.
 */

import type { Edge, Node } from '@xyflow/react'

/**
 * Index edges as a reverse adjacency map: ``target → [source, …]``.
 * Built once and reused for every per-node predecessor lookup.
 */
export const buildParentIndex = (edges: Edge[]): Record<string, string[]> => {
  const parents: Record<string, string[]> = {}
  edges.forEach((e) => {
    // eslint-disable-next-line security/detect-object-injection
    parents[e.target] = [...(parents[e.target] ?? []), e.source]
  })
  return parents
}

/**
 * All node ids transitively reachable upstream from ``nodeId`` via the
 * given parent index. Excludes ``nodeId`` itself.
 */
export const predecessorsOf = (
  nodeId: string,
  parents: Record<string, string[]>,
): Set<string> => {
  const reachable = new Set<string>()
  // eslint-disable-next-line security/detect-object-injection
  const queue = [...(parents[nodeId] ?? [])]
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (reachable.has(id)) continue
    reachable.add(id)
    // eslint-disable-next-line security/detect-object-injection
    ;(parents[id] ?? []).forEach((p) => queue.push(p))
  }
  return reachable
}

/**
 * Names of loop-item variables visible at ``nodeId`` — collected from
 * every ``loop`` node found upstream. A node that lives inside two
 * nested loops sees both item variable names.
 */
export const loopVariablesAtNode = (
  nodeId: string,
  nodes: Node[],
  parents: Record<string, string[]>,
): string[] => {
  const ancestors = predecessorsOf(nodeId, parents)
  const names = new Set<string>()
  nodes.forEach((n) => {
    if (!ancestors.has(n.id)) return
    const data = n.data as { type?: string; itemVariable?: string }
    if (data?.type === 'loop' && typeof data.itemVariable === 'string' && data.itemVariable) {
      names.add(data.itemVariable)
    }
  })
  return Array.from(names).sort()
}
